import React, { useState } from 'react';
import { Phone, Calendar, Star, CheckCircle, ArrowRight, Shield, DollarSign, HeadphonesIcon, Check, Search, Clock, Award, MessageCircle, FileText, Truck, Settings, Volume2, TrendingUp } from 'lucide-react';
import { businessInfo, services, whyChooseUs, reviews, guarantees, brands, pricingPackages, faqs, certifications, howItWorks, stats } from '../mockData';
import { toast } from 'sonner';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    vehicle: '',
    service: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    year: '',
    make: '',
    model: '',
    serviceType: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success("Message Sent!", {
      description: "We'll contact you shortly to schedule your installation.",
      duration: 4000,
    });
    setFormData({ name: '', phone: '', vehicle: '', service: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    toast.success("Quote Request Received!", {
      description: "We'll call you within 30 minutes with your personalized quote!",
      duration: 4000,
    });
    setQuoteForm({ year: '', make: '', model: '', serviceType: '' });
  };

  const handleQuoteChange = (e) => {
    setQuoteForm({ ...quoteForm, [e.target.name]: e.target.value });
  };

  return (
    <div className="home-page">
      {/* Floating Action Buttons */}
      <div className="floating-actions">
        <a href={`tel:${businessInfo.phone}`} className="floating-btn floating-call">
          <Phone size={24} />
          <span>Call Now</span>
        </a>
        <a href="#contact" className="floating-btn floating-chat">
          <MessageCircle size={24} />
          <span>Get Quote</span>
        </a>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background-effect">
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <span className="badge-icon">⚡</span>
              <span>ATLANTA'S #1 MOBILE CAR AUDIO</span>
            </div>
            
            <h1 className="hero-title">
              <span className="title-line title-line-1">Premium Car Audio</span>
              <span className="title-line title-line-2">Installed At</span>
              <span className="title-line title-line-3 title-highlight">Your Location</span>
            </h1>
            
            <p className="hero-subtitle">
              Skip the shop. We bring professional car stereo installation directly to your driveway. 
              <span className="subtitle-highlight"> Same-day service available.</span>
            </p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">5.0</div>
                <div className="stat-label">★★★★★ Rating</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Installs Done</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Satisfaction</div>
              </div>
            </div>
            
            <div className="hero-buttons">
              <a href={`tel:${businessInfo.phone}`} className="btn-primary btn-large btn-pulse">
                <Phone size={24} />
                <span className="btn-content">
                  <span className="btn-main-text">Call Now</span>
                  <span className="btn-sub-text">{businessInfo.phone}</span>
                </span>
              </a>
              <a href="#contact" className="btn-secondary btn-large">
                <Calendar size={24} />
                Get Free Quote
              </a>
            </div>
            
            <div className="hero-trust-badges">
              <div className="trust-badge">
                <CheckCircle size={20} />
                <span>Licensed & Insured</span>
              </div>
              <div className="trust-badge">
                <CheckCircle size={20} />
                <span>Same-Day Service</span>
              </div>
              <div className="trust-badge">
                <CheckCircle size={20} />
                <span>Lifetime Warranty</span>
              </div>
            </div>
          </div>
          
          <div className="hero-image-container">
            <div className="hero-image-frame">
              <img 
                src="https://cdn.pixabay.com/photo/2022/01/26/17/44/car-6969506_1280.jpg" 
                alt="Professional car audio installation - dashboard and stereo system"
                className="hero-image-main"
              />
              <div className="image-overlay-badge">
                <div className="overlay-badge-content">
                  <span className="overlay-badge-icon">🎵</span>
                  <span className="overlay-badge-text">Expert Audio Installation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Quote Tool Section */}
      <section className="quick-quote-section">
        <div className="quick-quote-container">
          <div className="quick-quote-header">
            <Search size={40} className="quote-icon" />
            <h2 className="quick-quote-title">Get Your Instant Quote in 60 Seconds</h2>
            <p className="quick-quote-subtitle">
              Tell us about your vehicle and get an accurate price estimate immediately
            </p>
          </div>
          
          <form onSubmit={handleQuoteSubmit} className="quick-quote-form">
            <div className="quote-form-row">
              <select
                name="year"
                value={quoteForm.year}
                onChange={handleQuoteChange}
                className="quote-input"
                required
              >
                <option value="">Select Year</option>
                {[2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              
              <select
                name="make"
                value={quoteForm.make}
                onChange={handleQuoteChange}
                className="quote-input"
                required
              >
                <option value="">Select Make</option>
                <option value="Honda">Honda</option>
                <option value="Toyota">Toyota</option>
                <option value="Ford">Ford</option>
                <option value="Chevrolet">Chevrolet</option>
                <option value="Nissan">Nissan</option>
                <option value="BMW">BMW</option>
                <option value="Mercedes">Mercedes</option>
                <option value="Other">Other</option>
              </select>
              
              <input
                type="text"
                name="model"
                placeholder="Model (e.g., Accord, Camry)"
                value={quoteForm.model}
                onChange={handleQuoteChange}
                className="quote-input"
                required
              />
              
              <select
                name="serviceType"
                value={quoteForm.serviceType}
                onChange={handleQuoteChange}
                className="quote-input"
                required
              >
                <option value="">What do you need?</option>
                {services.map(service => (
                  <option key={service.id} value={service.title}>{service.title}</option>
                ))}
              </select>
              
              <button type="submit" className="btn-primary btn-quote">
                Get My Quote
                <ArrowRight size={20} />
              </button>
            </div>
          </form>

          <div className="quote-benefits">
            <div className="quote-benefit">
              <Clock size={20} />
              <span>30-Min Response Time</span>
            </div>
            <div className="quote-benefit">
              <DollarSign size={20} />
              <span>No Hidden Fees</span>
            </div>
            <div className="quote-benefit">
              <CheckCircle size={20} />
              <span>Same-Day Service Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges / Certifications Section */}
      <section className="certifications-section">
        <div className="certifications-container">
          {certifications.map((cert) => {
            const Icon = require('lucide-react')[cert.icon];
            return (
              <div key={cert.id} className="certification-badge">
                <Icon size={32} />
                <div className="cert-text">
                  <h4>{cert.name}</h4>
                  <p>{cert.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section Divider with CTA */}
      <section className="cta-divider">
        <div className="cta-divider-content">
          <h3 className="cta-divider-title">Ready for Premium Car Audio?</h3>
          <p className="cta-divider-text">Join 500+ satisfied customers in Metro Atlanta</p>
          <a href={`tel:${businessInfo.phone}`} className="btn-primary btn-large">
            <Phone size={24} />
            Call {businessInfo.phone}
          </a>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-header">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Simple 4-step process from quote to incredible sound
          </p>
        </div>
        <div className="how-it-works-grid">
          {howItWorks.map((step, index) => {
            const Icon = require('lucide-react')[step.icon];
            return (
              <div key={step.id} className="how-it-works-step">
                <div className="step-number">{step.step}</div>
                <div className="step-icon-wrapper">
                  <Icon size={40} />
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {index < howItWorks.length - 1 && (
                  <div className="step-arrow">→</div>
                )}
              </div>
            );
          })}
        </div>
        <div className="stats-bar">
          {stats.map((stat, index) => (
            <div key={index} className="stat-box">
              <div className="stat-number-large">{stat.number}</div>
              <div className="stat-label-large">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Video Greeting Section */}
      <section className="video-greeting-section">
        <div className="video-greeting-content">
          <div className="video-greeting-text">
            <h2 className="video-greeting-title">Welcome to Drip Audio LLC</h2>
            <p className="video-greeting-description">
              Meet our team and see why customers across Metro Atlanta trust us 
              for professional mobile car audio installation. We bring expert 
              service directly to your location!
            </p>
            <div className="video-greeting-features">
              <div className="greeting-feature">
                <CheckCircle size={24} />
                <span>5.0 Star Google Rating</span>
              </div>
              <div className="greeting-feature">
                <CheckCircle size={24} />
                <span>Professional Installation</span>
              </div>
              <div className="greeting-feature">
                <CheckCircle size={24} />
                <span>Mobile Convenience</span>
              </div>
            </div>
          </div>
          <div className="video-greeting-player">
            <div className="video-container">
              <video
                className="video-autoplay"
                autoPlay
                muted
                loop
                playsInline
                poster="https://images.unsplash.com/photo-1592570714618-15e2d4719c6c"
              >
                <source src="/videos/welcome-video.mp4" type="video/mp4" />
                {/* Fallback image if video doesn't load */}
                <img 
                  src="https://images.unsplash.com/photo-1592570714618-15e2d4719c6c" 
                  alt="Professional car stereo installation"
                  className="video-fallback"
                />
              </video>
              <div className="video-autoplay-overlay">
                <div className="video-badge">
                  <span className="badge-pulse"></span>
                  <span className="badge-text">NOW PLAYING</span>
                </div>
              </div>
            </div>
            <p className="video-caption">
              Watch our introduction to Drip Audio LLC • Professional Mobile Car Audio Installation
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="section-header">
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">
            Comprehensive car audio solutions delivered to your location
          </p>
        </div>
        <div className="services-grid">
          {services.map((service) => {
            const Icon = require('lucide-react')[service.icon];
            return (
              <div key={service.id} className="service-card">
                <div className="service-image-container">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="service-image"
                  />
                  <div className="service-icon-overlay">
                    <Icon size={32} />
                  </div>
                </div>
                <div className="service-content">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-section">
        <div className="why-content">
          <div className="why-text">
            <h2 className="section-title">Why Choose Drip Audio LLC</h2>
            <p className="section-subtitle">
              We deliver professional mobile car audio installation with
              unmatched convenience and quality
            </p>
            <div className="why-features">
              {whyChooseUs.map((feature) => {
                const Icon = require('lucide-react')[feature.icon];
                return (
                  <div key={feature.id} className="why-feature">
                    <div className="why-icon">
                      <Icon size={28} />
                    </div>
                    <div className="why-text-content">
                      <h3 className="why-feature-title">{feature.title}</h3>
                      <p className="why-feature-description">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <a href="#contact" className="btn-primary btn-large">
              <Calendar size={24} />
              Schedule Installation
            </a>
          </div>
          <div className="why-image">
            <img
              src="https://images.pexels.com/photos/13972228/pexels-photo-13972228.jpeg"
              alt="Mobile car stereo installation service"
              className="why-image-main"
            />
          </div>
        </div>
      </section>

      {/* Pricing Packages Section */}
      <section className="pricing-section">
        <div className="section-header">
          <h2 className="section-title">Installation Packages</h2>
          <p className="section-subtitle">
            Transparent pricing with no hidden fees • Final quote before work begins
          </p>
        </div>
        <div className="pricing-grid">
          {pricingPackages.map((pkg) => (
            <div key={pkg.id} className={`pricing-card ${pkg.popular ? 'pricing-popular' : ''}`}>
              {pkg.popular && <div className="popular-badge">MOST POPULAR</div>}
              <h3 className="pricing-name">{pkg.name}</h3>
              <div className="pricing-price">{pkg.price}</div>
              <ul className="pricing-features">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="pricing-feature">
                    <Check size={18} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <a href={`tel:${businessInfo.phone}`} className="btn-secondary btn-full">
                Get Quote
              </a>
            </div>
          ))}
        </div>
        <p className="pricing-note">
          *Final pricing depends on vehicle model, required parts, and installation complexity. 
          Mobile service fee may apply outside metro area. Call for exact quote.
        </p>
      </section>

      {/* Guarantees Section */}
      <section className="guarantees-section">
        <div className="section-header">
          <h2 className="section-title">Our Guarantee to You</h2>
          <p className="section-subtitle">
            Professional service backed by real warranties and support
          </p>
        </div>
        <div className="guarantees-grid">
          {guarantees.map((guarantee) => {
            const Icon = require('lucide-react')[guarantee.icon];
            return (
              <div key={guarantee.id} className="guarantee-card">
                <div className="guarantee-icon">
                  <Icon size={32} />
                </div>
                <h3 className="guarantee-title">{guarantee.title}</h3>
                <p className="guarantee-description">{guarantee.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Brands Section */}
      <section className="brands-section">
        <div className="section-header">
          <h2 className="section-title">Brands We Work With</h2>
          <p className="section-subtitle">
            Authorized dealer for premium car audio brands
          </p>
        </div>
        <div className="brands-grid">
          {brands.map((brand, index) => (
            <div key={index} className="brand-item">
              {brand}
            </div>
          ))}
        </div>
      </section>

      {/* Seamless Transition - Social Proof */}
      <section className="social-proof-banner">
        <div className="social-proof-content">
          <div className="proof-item">
            <TrendingUp size={40} />
            <div className="proof-text">
              <h3>500+ Happy Customers</h3>
              <p>Across Metro Atlanta</p>
            </div>
          </div>
          <div className="proof-divider"></div>
          <div className="proof-item">
            <Star size={40} fill="var(--brand-primary)" color="var(--brand-primary)" />
            <div className="proof-text">
              <h3>5.0 Star Rating</h3>
              <p>Verified Google Reviews</p>
            </div>
          </div>
          <div className="proof-divider"></div>
          <div className="proof-item">
            <Award size={40} />
            <div className="proof-text">
              <h3>MECP Certified</h3>
              <p>Professional Technicians</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-section">
        <div className="section-header">
          <h2 className="section-title">Customer Reviews</h2>
          <p className="section-subtitle">
            See what our satisfied customers have to say
          </p>
        </div>
        <div className="reviews-grid">
          {reviews.map((review) => (
            <div key={review.id} className="review-card">
              <div className="review-stars">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                ))}
              </div>
              <p className="review-text">"{review.text}"</p>
              <div className="review-author">
                <span className="review-name">{review.name}</span>
                <span className="review-date">{review.date}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-header">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Everything you need to know about our mobile installation service
          </p>
        </div>
        <div className="faq-grid">
          {faqs.map((faq) => (
            <div key={faq.id} className="faq-item">
              <h4 className="faq-question">{faq.question}</h4>
              <p className="faq-answer">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service Area Section */}
      <section className="service-area-section">
        <div className="service-area-content">
          <CheckCircle size={60} className="area-icon" />
          <h2 className="area-title">Serving Metro Atlanta</h2>
          <p className="area-description">
            Professional mobile car audio installation serving {businessInfo.location} and surrounding areas
          </p>
          <div className="area-list">
            {businessInfo.serviceArea.map((area, index) => (
              <div key={index} className="area-item">
                <CheckCircle size={20} />
                <span>{area}</span>
              </div>
            ))}
          </div>
          
          {/* Google Map */}
          <div className="service-area-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424396.8039440939!2d-84.55991288488113!3d33.74899096870453!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f5045d6993098d%3A0x66fede2f990b630b!2sAtlanta%2C%20GA!5e0!3m2!1sen!2sus!4v1705087254836!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: '2px solid var(--border-subtle)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Service Area Map - Metro Atlanta"
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-content">
          <div className="contact-info">
            <h2 className="contact-title">Get Your Free Quote</h2>
            <p className="contact-description">
              Ready to upgrade your car audio? Contact us today for a free
              consultation and quote. We'll come to you!
            </p>
            <div className="contact-details">
              <div className="contact-detail-item">
                <Phone size={28} />
                <div>
                  <h4>Call Us</h4>
                  <a href={`tel:${businessInfo.phone}`} className="contact-link-primary">
                    {businessInfo.phone}
                  </a>
                </div>
              </div>
              <div className="contact-detail-item">
                <Star size={28} />
                <div>
                  <h4>Google Rating</h4>
                  <p>{businessInfo.rating} ★★★★★</p>
                </div>
              </div>
            </div>
            <a href={`tel:${businessInfo.phone}`} className="btn-primary btn-large">
              <Phone size={24} />
              Call Now for Free Quote
            </a>
          </div>

          <div className="contact-form-container">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3 className="form-title">Book Your Appointment</h3>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                required
              />
              <input
                type="text"
                name="vehicle"
                placeholder="Vehicle (Year, Make, Model)"
                value={formData.vehicle}
                onChange={handleChange}
                className="form-input"
                required
              />
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Select Service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.title}>
                    {service.title}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-primary btn-full">
                Request Appointment
                <ArrowRight size={20} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;